import { useEffect, useRef } from "react";
import * as Tone from "tone";

const NUM_BARS = 64;
const INNER_R = 34; // just outside the 60px play button (r=30)
const BAR_MAX = 27; // max extension; volume arc sits at r=75
const SIZE = 200;

interface Props {
  analyserRef: React.MutableRefObject<Tone.Analyser | null>;
  playing: boolean;
  hue: number;
  sat: number;
  lit: number;
}

export function OrbVisualiser({ analyserRef, playing, hue, sat, lit }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const stateRef = useRef({ playing, hue, sat, lit });

  useEffect(() => {
    stateRef.current = { playing, hue, sat, lit };
  }, [playing, hue, sat, lit]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.style.width = `${SIZE}px`;
    canvas.style.height = `${SIZE}px`;
    canvas.width = SIZE * dpr;
    canvas.height = SIZE * dpr;

    const ctx = canvas.getContext("2d")!;
    ctx.scale(dpr, dpr);

    const cx = SIZE / 2;
    const cy = SIZE / 2;
    let rafId: number;

    function draw() {
      ctx.clearRect(0, 0, SIZE, SIZE);
      const { playing: p, hue: h } = stateRef.current;
      const analyser = analyserRef.current;

      if (analyser && p) {
        const { sat: s, lit: l } = stateRef.current;
        const data = analyser.getValue() as Float32Array;
        ctx.lineWidth = 1.5;
        ctx.lineCap = "round";
        ctx.shadowColor = `hsl(${h}, ${Math.min(100, s + 15)}%, ${l}%)`;
        ctx.shadowBlur = 5;

        for (let i = 0; i < NUM_BARS; i++) {
          const angle = (i / NUM_BARS) * Math.PI * 2 - Math.PI / 2;
          const db = data[i] as number;
          if (!isFinite(db)) continue;
          // Map dB: -90 (silence) → 0, -25 (loud) → 1
          const t = Math.max(0, Math.min(1, (db + 90) / 65));
          if (t < 0.02) continue;

          const r2 = INNER_R + t * BAR_MAX;
          ctx.beginPath();
          ctx.moveTo(cx + INNER_R * Math.cos(angle), cy + INNER_R * Math.sin(angle));
          ctx.lineTo(cx + r2 * Math.cos(angle), cy + r2 * Math.sin(angle));
          ctx.strokeStyle = `hsla(${h}, ${s}%, ${l}%, ${0.35 + t * 0.65})`;
          ctx.stroke();
        }

        ctx.shadowBlur = 0;
      }

      rafId = requestAnimationFrame(draw);
    }

    rafId = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(rafId);
  }, [analyserRef]);

  return (
    <canvas
      ref={canvasRef}
      style={{ position: "absolute", top: 0, left: 0, pointerEvents: "none" }}
    />
  );
}
